import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import useSound from 'use-sound'

import OptionsContext from '../../utils/OptionsContext'
import useAnimation from '../../utils/hooks/useAnimation'

import { Button } from '../../components'
import ChevronRight from '../Icons/ChevronRight'
import styles from './BoostQuiz.module.scss'
import winSound from '../../assets/sounds/fx/10-correct-quiz-answer.mp3'
import loseSound from '../../assets/sounds/fx/11-incorrect-quiz-answer.mp3'

const BoostQuiz = ({ setResult, quiz, resultElement, onModalClose, result }) => {
  const [selectedAnswer, setSelectedAnswer] = useState('')
  const { t } = useTranslation()
  const [animate, trigger] = useAnimation({ x: 5 })
  const { shouldReduceMotion, soundEffectsOn, effectsVolume } = useContext(OptionsContext).options
  const [playWinSound] = useSound(winSound, { volume: effectsVolume })
  const [playLoseSound] = useSound(loseSound, { volume: effectsVolume })
  let timeout

  const correctAnswer = quiz.answers.find(answer => answer.isCorrect)
  
  function checkAnswer(e) {
    e.preventDefault()
    if (selectedAnswer.isCorrect) {
      setResult('win')
      timeout = setTimeout(() => {
        soundEffectsOn && playWinSound()
      }, shouldReduceMotion ? 0 : 550)
      return
    }
    setResult('loss')
    timeout = setTimeout(() => {
      soundEffectsOn && playLoseSound()
    }, shouldReduceMotion ? 0 : 665)
  }
  
  function renderStyle(answer) {
    let style = styles.option
    if (selectedAnswer.text === answer.text) {
      style += ` ${styles.marked}`
    }
    if (result) {
      style += ` ${styles.disabled}`
    }
    return style
  }

  useEffect(() => {
    return () => {
      clearTimeout(timeout)
    }
  }, [timeout])

  const divVariants = {
    show: { 
      transition: {
        staggerChildren: shouldReduceMotion ? .1 : .4,
        delayChildren: shouldReduceMotion ? .1 : .8,
      }
    },
    hide: { 
      transition: {
        staggerChildren: .1,
        staggerDirection: -1,
        delayChildren: .4
      }
    },
    check: { 
      padding: 0,
      margin: 0,
      transition: {
        margin: { delay: .7, duration: .2 },
        padding: { delay: .7, duration: .2 },
        staggerChildren: .2,
        staggerDirection: -1,
      }
    }
  }

  const buttonVariants = {
    show: { 
      y: 0, 
      opacity: 1,
      height: 'auto'
    },
    hide: { 
      y: -20, 
      opacity: 0, 
      height: '1px',
    },
    check: { 
      y: 0, 
      x: -40, 
      opacity: 0,
      height: '1px',
      padding: 0,
      margin: 0,
      border: '0px',
      transition: {
        height: { delay: .7, duration: .2 },
        margin: { delay: .7, duration: .2 },
        padding: { delay: .7, duration: .2 },
        border: { delay: .7, duration: .2 },
      }
    }
  }

  return(
    <article className={styles.wrapper}>
      <form action="" onSubmit={checkAnswer}>
        <div className={styles.quiz}>
          <h1>{quiz.question}</h1>
          {((shouldReduceMotion && !result) || !shouldReduceMotion) && <motion.div 
            className={styles.answers}
            variants={divVariants}
            initial="hide"
            animate={result ? 'check' : 'show'}
            exit="hide"
          >
            {
              quiz.answers.map(answer => (
                <motion.label 
                  variants={buttonVariants} 
                  key={answer.text}
                  className={renderStyle(answer)}
                  htmlFor={answer.text} 
                >{answer.text}
                  <input  
                    className='sr-only'
                    onChange={() => setSelectedAnswer(answer)} 
                    type="radio" 
                    name="answer" 
                    id={answer.text}
                    value={answer.text}
                    checked={selectedAnswer.text === answer.text}
                  />
                </motion.label>
              ))
            }
          </motion.div>}
          {result && (
            <div className={styles.answers}>
              <motion.p 
                className={renderStyle(correctAnswer)}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: shouldReduceMotion ? 1 : [.4, .7, 1.1, 1, 1.1, 1] }}
                transition={{ 
                  opacity: {
                    duration: shouldReduceMotion ? .1 : 1, delay: shouldReduceMotion ? 0 : 1
                  },
                  scale: {
                    duration: shouldReduceMotion ? 0 : 1, delay: shouldReduceMotion ? 0 : 1
                  },
                }} 
              >
                {correctAnswer.text}
              </motion.p>
              <motion.div
                className={styles.result}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: shouldReduceMotion ? .1 : 1, delay: shouldReduceMotion ? 0 : 1.4 }} 
              >
                {resultElement}
              </motion.div>
            </div>
          )}
        </div>
        {result ? 
          <Button type="button" onClick={onModalClose} size='large'>
            {t('common:confirm')}
          </Button> : 
          <Button 
            variant="primary" 
            size='large' 
            type="submit" 
            disabled={!selectedAnswer}
            onMouseEnter={trigger}
          >
            {t('common:boost:answer')}
            <motion.span animate={animate}>
              <ChevronRight />
            </motion.span>
          </Button>}
      </form>
    </article>
  )
}

export default BoostQuiz